import React, { useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { Modal, InputGroup, Form, Button } from "react-bootstrap";
import axios from "axios";

import API_ROUTE from "../../utils/apiRoute";
import { GlobalContext } from "../../GlobalState";
import Select from "react-select";

const RequestModal = ({
  isShowing,
  toggle,
  userName,
  userRole,
  requestReservation,
  inputRef,
  selectRef,
  userRef,
  spaceId,
  zoomTitleRef,
  reserveZoomRef,
}) => {
  const [reasons, setReasons] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const { selectedSchoolCode, setSelectedSchoolCode } =
    useContext(GlobalContext);
  const [spaceNotes, setSpaceNotes] = useState("");
  const [requireZoomLicense, setRequireZoomLicense] = useState(false);
  const [isVirtualZoomRoom, setIsVirtualZoomRoom] = useState(false);
  const [autoApproveReservations, setAutoApproveReservations] = useState(false);
  const [reserveZoom, setReserveZoom] = useState('sim');

  const isManager = () => {
    return (
      userRole === "space-manager" ||
      userRole === "system-manager" ||
      userRole === "system-master-manager"
    );
  };

  useEffect(() => {
    let didCancel = false;

    (async () => {
      if (selectedSchoolCode) {
        await axios
          .get(`${API_ROUTE}reason/school/${selectedSchoolCode}`, {
            withCredentials: true,
          })
          .then((res) => {
            if (didCancel) return;
            setReasons(res.data.data.allReasonsBySchool);
          });

        await axios
          .get(`${API_ROUTE}employee/getAll/school/${selectedSchoolCode}`, {
            withCredentials: true,
          })
          .then((res) => {
            if (didCancel) return;
            const allUsers = res.data.data.employees;
            const allUsersOptions = allUsers.map((user) => {
              return {
                value: user._id,
                label: `${user.name} (${user.email.split("@")[0]})`,
              };
            });
            setAllUsers(allUsersOptions);
          })
          .catch((err) => console.error(err));
      }
    })();

    return () => {
      didCancel = true;
    };
  }, [selectedSchoolCode, setSelectedSchoolCode]);

  useEffect(() => {
    if (!spaceId || !inputRef?.current) return;

    let didCancel = false;

    (async () => {
      await axios
        .get(`${API_ROUTE}space/${spaceId}`, {
          withCredentials: true,
        })
        .then((res) => {
          if (didCancel) return;
          const spaceData = res.data.data.spaceData;
          inputRef.current.value = spaceData.template || "";
          setSpaceNotes(spaceData.notes || "");
          setRequireZoomLicense(!!spaceData.requireZoomLicense);
          setIsVirtualZoomRoom(spaceData.type === "Sala Virtual Zoom");
          setAutoApproveReservations(
            spaceData.autoApproveReservations === true
          );
          setReserveZoom('sim');
        });
    })();

    return () => {
      didCancel = true;
    };
  }, [inputRef, spaceId]);

  const buildReasonsOptions = () => {
    const optionsArr = React.Children.toArray(
      reasons.map((val) => {
        return <option>{val.designation}</option>;
      })
    );
    return optionsArr;
  };

  const isZoomMeetingRoom = requireZoomLicense && !isVirtualZoomRoom;
  const isZoomVirtualRoom = requireZoomLicense && isVirtualZoomRoom;

  const renderManagerUserField = () =>
    isManager() ? (
      <InputGroup style={{ width: "100%" }}>
        <InputGroup.Prepend>
          <InputGroup.Text>Utilizador</InputGroup.Text>
        </InputGroup.Prepend>
        <Select
          styles={{
            container: (provided) => ({
              ...provided,
              flex: 1,
            }),
          }}
          ref={userRef}
          options={allUsers}
          placeholder="Se necessário selecione o utilizador"
          isClearable={true}
          onChange={(selectedOption) => {
            if (userRef && userRef.current) {
              userRef.current.value = selectedOption
                ? selectedOption.value
                : null;
            }
          }}
        />
      </InputGroup>
    ) : null;

  const renderAutoApprovalNote = () =>
    autoApproveReservations ? (
      <p className="mb-3">
        Este espaço tem aprovação automática: se o horário estiver livre, a
        reserva fica logo confirmada, sem esperar pelo gestor.
      </p>
    ) : null;

  const renderNotesField = () =>
    spaceNotes ? (
      <InputGroup className="mb-3">
        <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
          <strong>Notas:</strong>{"\n"}{spaceNotes}
        </p>
      </InputGroup>
    ) : null;

  return isShowing
    ? ReactDOM.createPortal(
        <React.Fragment>
          <Modal show={isShowing} onHide={toggle}>
            <Modal.Header closeButton>
              <Modal.Title>Solicitar Reserva</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {renderAutoApprovalNote()}
              {isZoomMeetingRoom ? (
                <>
                  <p className="mb-3"><strong>Sala de reuniões/auditório com vinculação a Conta Zoom:</strong></p>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Motivo</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control ref={selectRef} as="select" aria-label="Motivo">
                      {buildReasonsOptions()}
                    </Form.Control>
                  </InputGroup>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Reservar conta Zoom</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      ref={reserveZoomRef}
                      as="select"
                      aria-label="Reservar conta Zoom"
                      onChange={(e) => setReserveZoom(e.target.value)}
                    >
                      <option value="sim">Sim</option>
                      <option value="nao">Não</option>
                    </Form.Control>
                  </InputGroup>

                  {reserveZoom === 'sim' && (
                    <InputGroup className="mb-3">
                      <InputGroup.Prepend>
                        <InputGroup.Text>Titulo da sessão Zoom</InputGroup.Text>
                      </InputGroup.Prepend>
                      <Form.Control ref={zoomTitleRef} type="text" aria-label="Titulo da sessão Zoom" />
                    </InputGroup>
                  )}

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Descrição</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      ref={inputRef}
                      as="textarea"
                      aria-label="Descrição"
                      rows={5}
                      style={{ overflowY: "auto" }}
                    />
                  </InputGroup>

                  {renderNotesField()}
                  {renderManagerUserField()}
                </>
              ) : isZoomVirtualRoom ? (
                <>
                  <p className="mb-3"><strong>Sala virtual Zoom:</strong></p>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Motivo</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control ref={selectRef} as="select" aria-label="Motivo">
                      {buildReasonsOptions()}
                    </Form.Control>
                  </InputGroup>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Titulo da sessão Zoom</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control ref={zoomTitleRef} type="text" aria-label="Titulo da sessão Zoom" />
                  </InputGroup>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Descrição</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      ref={inputRef}
                      as="textarea"
                      aria-label="Descrição"
                      rows={5}
                      style={{ overflowY: "auto" }}
                    />
                  </InputGroup>

                  {renderNotesField()}
                  {renderManagerUserField()}
                </>
              ) : (
                <>
                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text id="inputGroup-sizing-default">
                        Nome
                      </InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      aria-label="Default"
                      disabled
                      defaultValue={userName}
                      aria-describedby="inputGroup-sizing-default"
                    />
                  </InputGroup>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Motivo</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control ref={selectRef} as="select" aria-label="With textarea">
                      {buildReasonsOptions()}
                    </Form.Control>
                  </InputGroup>

                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text>Descrição</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      ref={inputRef}
                      as="textarea"
                      aria-label="With textarea"
                      rows={5}
                      style={{ overflowY: "auto" }}
                    />
                  </InputGroup>

                  {renderNotesField()}
                  {renderManagerUserField()}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="danger" onClick={toggle}>
                Cancelar
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  requestReservation();
                  toggle();
                }}
              >
                Confirmar
              </Button>
            </Modal.Footer>
          </Modal>
        </React.Fragment>,
        document.body
      )
    : null;
};

export default RequestModal;
